import React, { createContext, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import useSecurity from "../hooks/useSecurity";
import { UserContext } from "./UserContext";
import { ConfigContext } from "../security/ConfigContext";
import ErrorRedirect from "../components/error-redirect/ErrorRedirect";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { UserRoles } from "../constants/UserRoles";
import _ from "lodash";
import useAxios from "../adapter/useAxios";
import { ASSET_TYPE, EMPTY_CANTEMO_FILTERS, MEDIA_SEARCH, PROXY_MANIFESTATION_TYPES } from "../constants/MediaSearch";
import { buildCantemoValidatePayload } from "../utils/MediaRequestUtils";
import { CREATE_MEDIA, NAMESPACE_TO_ID_TYPE_MAPPING, REGION_PATHS } from "../constants/CreateMediaRequest";
import { normalizeCantemoComponents } from "../components/mediaRequest/createMediaRequest/util";
import ConfigUtils from "../utils/ConfigUtils";
import SocketUtils from "../utils/SocketUtils";
import { SocketContext } from "./SocketContext";

const initialState = {
  loading: false,
  pageError: false,
  options: [],
  noOptionsFound: false,
  recordType: "",
  selectedOption: {},
  assetData: null,
  isAssetDataLoading: false,
  suggestString: "",
  validAssets: {},
  videoContainerId: {},
  selectedFiles: {},
  activeStep: 0,
  idAccordionExpanded: {},
  titleAccordionExpanded: {},
  transferRegion: {},
  requestName: "",
  renamedFilename: {},
  saveToCart: {},
  odsEditsData: {},
  isOdsEditsDataLoading: {},
  odsEditsFetchError: {},
  isMrSubmitError: false,
  idType: [],
  idQueryType: "",
  idTypeValues: "",
  cantemoFilters: { ...EMPTY_CANTEMO_FILTERS },
  cantemoPage: 0,
  cantemoTotal: null,
  odsSearchIdsData: {},
  seasonData: [],
  seasonDataError: "",
  isSeasonDataLoading: false,
  episodeData: {},
  episodeDataError: {},
  isEpisodeDataLoading: {},
  isPromoLoading: false,
  isCantemoLoading: false,
  promoError: {},
  isProxyLoading: false,
};

export const MediaRequestContext = createContext({
  loading: initialState.loading,
  pageError: initialState.pageError,
  fetchNetworks: () => {},
  options: initialState.options,
  _setOptions: (options) => {},
  recordType: initialState.recordType,
  _setRecordType: (recordType) => {},
  fetchSearchOptions: (value, recordType) => {},
  selectedOption: initialState.selectedOption,
  _setSelectedOption: (selectedOption) => {},
  assetData: initialState.assetData,
  isAssetDataLoading: initialState.isAssetDataLoading,
  fetchAssets: (selectedOption) => {},
  suggestString: initialState.suggestString,
  _setSuggestString: (suggestString) => {},
  validAssets: initialState.validAssets,
  _setValidAssets: (asset) => {},
  videoContainerId: initialState.videoContainerId,
  _setVideoContainerId: (videoContainerId) => {},
  selectedFiles: initialState.selectedFiles,
  _setSelectedFiles: (selectedFiles) => {},
  handleSelectFile: (e, file, type, manifestation, assetTitle, editRate, entityType, idType = null) => {},
  activeStep: initialState.activeStep,
  _setActiveStep: (activeStep) => {},
  idAccordionExpanded: initialState.idAccordionExpanded,
  _setIdAccordionExpanded: (idAccordionExpanded) => {},
  titleAccordionExpanded: initialState.titleAccordionExpanded,
  _setTitleAccordionExpanded: (titleAccordionExpanded) => {},
  transferRegion: initialState.transferRegion,
  _setTransferRegion: (transferRegion) => {},
  submitMediaRequest: () => {},
  requestName: initialState.requestName,
  _setRequestName: (requestName) => {},
  renamedFilename: initialState.renamedFilename,
  _setRenamedFilename: (renamedFilename) => {},
  saveToCart: initialState.saveToCart,
  _setSaveToCart: (saveToCart) => {},
  odsEditsData: initialState.odsEditsData,
  _setOdsEditsData: (odsEditsData) => {},
  fetchEditsFromODS: (relatedRecords, guid) => {},
  isOdsEditsDataLoading: initialState.isOdsEditsDataLoading,
  odsEditsFetchError: initialState.odsEditsFetchError,
  isMrSubmitError: initialState.isMrSubmitError,
  _setIsMrSubmitError: (isMrSubmitError) => {},
  idType: initialState.idType,
  _setIdType: (idType) => {},
  idQueryType: initialState.idQueryType,
  _setIdQueryType: (idQueryType) => {},
  idTypeValues: initialState.idTypeValues,
  _setIdTypeValues: (idTypeValues) => {},
  cantemoFilters: initialState.cantemoFilters,
  _setCantemoFilters: (cantemoFilters) => {},
  cantemoPage: initialState.cantemoPage,
  _setCantemoPage: (cantemoPage) => {},
  cantemoTotal: initialState.cantemoTotal,
  searchCantemoPage: (page) => {},
  searchMediaById: () => {},
  _setOdsSearchIdsData: (odsSearchIdsData) => {},
  fetchEdits: (guid) => {},
  getAssetType: (entityType) => {},
  seasonData: initialState.seasonData,
  seasonDataError: initialState.seasonDataError,
  isSeasonDataLoading: initialState.isSeasonDataLoading,
  callSearchById: (payload) => {},
  episodeData: initialState.episodeData,
  episodeDataError: initialState.episodeDataError,
  isEpisodeDataLoading: initialState.isEpisodeDataLoading,
  _setSeasonData: (seasonData) => {},
  _setEpisodeData: (episodeData) => {},
  isPromoLoading: initialState.isPromoLoading,
  _setIsPromoLoading: (isPromoLoading) => {},
  isCantemoLoading: initialState.isCantemoLoading,
  _setIsCantemoLoading: (isCantemoLoading) => {},
  promoError: initialState.promoError,
  _setPromoError: (promoError) => {},
  isProxyLoading: initialState.isProxyLoading,
  noOptionsFound: initialState.noOptionsFound,
  getSourceId: (manifestationObj) => "",
  getSourceIdNamespace: (manifestationObj) => "",
});

export const MediaRequestContextProvider = ({ children }) => {
  const [loading, setLoading] = useState(initialState.loading);
  const [pageError, setPageError] = useState(initialState.pageError);
  const [options, setOptions] = useState(initialState.options);
  const [recordType, setRecordType] = useState(initialState.recordType);
  const [selectedOption, setSelectedOption] = useState(
    initialState.selectedOption
  );
  const [suggestString, setSuggestString] = useState(
    initialState.suggestString
  );
  const { userRole, isUserLoading } = useContext(UserContext);  
  const { userInfo } = useSecurity();
  const [config] = useContext(ConfigContext);
  const { _setMessageToSocket } = useContext(SocketContext);
  const [assetData, setAssetData] = useState(initialState.assetData);
  const [isAssetDataLoading, setIsAssetDataLoading] = useState(
    initialState.isAssetDataLoading
  );
  const [validAssets, setValidAssets] = useState(initialState.validAssets);
  const [videoContainerId, setVideoContainerId] = useState(initialState.videoContainerId);
  const [selectedFiles, setSelectedFiles] = useState(initialState.selectedFiles);
  const [activeStep, setActiveStep] = useState(initialState.activeStep);
  const [idAccordionExpanded, setIdAccordionExpanded] = useState(initialState.idAccordionExpanded);
  const [titleAccordionExpanded, setTitleAccordionExpanded] = useState(initialState.titleAccordionExpanded)
  const [transferRegion, setTransferRegion] = useState(initialState.transferRegion);
  const [requestName, setRequestName] = useState(initialState.requestName);
  const [renamedFilename, setRenamedFilename] = useState(initialState.renamedFilename);
  const [saveToCart, setSaveToCart] = useState(initialState.saveToCart);
  const [odsEditsData, setOdsEditsData] = useState(initialState.odsEditsData);
  const [odsEditsFetchError, setOdsEditsFetchError] = useState(initialState.odsEditsFetchError);
  const [isOdsEditsDataLoading, setIsOdsEditsDataLoading] = useState(initialState.isOdsEditsDataLoading);
  const [isMrSubmitError, setIsMrSubmitError] = useState(initialState.isMrSubmitError);
  const [idType, setIdType] = useState(initialState.idType);
  const [idQueryType, setIdQueryType] = useState(initialState.idQueryType);
  const [idTypeValues, setIdTypeValues] = useState(initialState.idTypeValues);
  const [cantemoFilters, setCantemoFilters] = useState(initialState.cantemoFilters);
  const [cantemoPage, setCantemoPage] = useState(initialState.cantemoPage);
  const [cantemoTotal, setCantemoTotal] = useState(initialState.cantemoTotal);
  const [odsSearchIdsData, setOdsSearchIdsData] = useState(initialState.odsSearchIdsData);
  const [seasonData, setSeasonData] = useState(initialState.seasonData);
  const [seasonDataError, setSeasonDataError] = useState(initialState.seasonDataError);
  const [isSeasonDataLoading, setIsSeasonDataLoading] = useState(initialState.isSeasonDataLoading);
  const [episodeData, setEpisodeData] = useState(initialState.episodeData);
  const [episodeDataError, setEpisodeDataError] = useState(initialState.episodeDataError);
  const [isEpisodeDataLoading, setIsEpisodeDataLoading] = useState(initialState.isEpisodeDataLoading);
  const [promoError, setPromoError] = useState(initialState.promoError);
  const [isPromoLoading, setIsPromoLoading] = useState(initialState.isPromoLoading);
  const [isCantemoLoading, setIsCantemoLoading] = useState(initialState.isCantemoLoading);
  const [isProxyLoading, setIsProxyLoading] = useState(initialState.isProxyLoading);
  const [noOptionsFound, setNoOptionsFound] = useState(initialState.noOptionsFound);
  const { get, post } = useAxios();
  const history = useHistory();
  const regionDetails = ConfigUtils.getTransferRegions();
  const aocConfigs = ConfigUtils.getAocConfigs();
  
  const _setSaveToCart = (saveToCart) => {
    setSaveToCart(saveToCart);
  };

  const _setActiveStep = (activeStep) => {
    setActiveStep(activeStep);
  };

  const _setOdsSearchIdsData = (odsSearchIdsData) => {
    setOdsSearchIdsData(odsSearchIdsData);
  };

  const _setIdAccordionExpanded = (idAccordionExpanded) => {
    setIdAccordionExpanded(idAccordionExpanded);
  };

  const _setTitleAccordionExpanded = (titleAccordionExpanded) => {
    setTitleAccordionExpanded(titleAccordionExpanded);
  };

  const _setSelectedFiles = (selectedFiles) => {
    setSelectedFiles(selectedFiles);
  };

  const _setRenamedFilename = (renamedFilename) => {
    setRenamedFilename(renamedFilename);
  };

  const _setVideoContainerId = (videoContainerId) => {
    setVideoContainerId(videoContainerId);
  };

  const _setTransferRegion = (transferRegion) => {
    setTransferRegion(transferRegion);
  };

  const _setRecordType = (recordType) => {
    setRecordType(recordType);
  };

  const _setIdType = (idType) => {
    setIdType(idType);
  };

  const _setIdQueryType = (idQueryType) => {
    setIdQueryType(idQueryType)
  };

  const _setIdTypeValues = (idTypeValues) => {
    setIdTypeValues(idTypeValues);
  };

  const _setCantemoFilters = (cantemoFilters) => {
    setCantemoFilters(cantemoFilters);
  };

  const _setCantemoPage = (cantemoPage) => {
    setCantemoPage(cantemoPage);
  };

  const applyCantemoFilterResults = (success, totals, page) => {
    const hasResults = Object.values(success || {}).some((records) =>
      Array.isArray(records) ? records.length : Boolean(records)
    );
    if (!hasResults) {
      setPromoError({title: "Not Found", message: "No files found for entered Cantemo value"});
      return false;
    }
    const total = totals ? Object.values(totals)[0] : null;
    setCantemoTotal(typeof total === "number" ? total : null);
    setCantemoPage(page);
    _setValidAssets(buildCantemoValidAssets(success, {}));
    return true;
  };

  const searchCantemoPage = (page) => {
    const { validateMultipleEdits } = config.apiUrls;
    const payload = buildCantemoValidatePayload(cantemoFilters, page);
    setIsCantemoLoading(true);
    post({
      url: validateMultipleEdits,
      data: payload,
    })
      .then((response) => {
        applyCantemoFilterResults(response?.details?.success, response?.details?.cantemoTotals, page);
      })
      .catch((error) => {
        console.log(error);
        if (error?.response?.status === 400) {
          setPromoError({title: "Not Found", message: "No files found for entered Cantemo value"});
        } else {
          setPromoError({title: "Network Error", message: "Error in finding Cantemo Data"});
        }
      })
      .finally(() => {
        setIsCantemoLoading(false);
      });
  };

  const _setOptions = (options) => {
    setOptions(options);
  };

  const _setSelectedOption = (selectedOption) => {
    setSelectedOption(selectedOption);
    if (selectedOption) {
      if (recordType === ASSET_TYPE.DMI_TITLE) {
        setIsPromoLoading(true);
        fetchDMIAssets(selectedOption);
      } else if (recordType === ASSET_TYPE.CANTEMO) {
        setIsPromoLoading(true);
        fetchCantemoAssets(selectedOption);
      } else {
        fetchAssets(selectedOption);
      }
    }
  };

  const _setOdsEditsData = (odsEditsData) => {
    setOdsEditsData(odsEditsData)
  };

  const _setSeasonData = (seasonData) => {
    setSeasonData(seasonData);
  };

  const _setEpisodeData = (episodeData) => {
    setEpisodeData(episodeData);
  };

  const _setIsPromoLoading = (isPromoLoading) => {
    setIsPromoLoading(isPromoLoading);
  };

  const _setIsCantemoLoading = (isCantemoLoading) => {
    setIsCantemoLoading(isCantemoLoading);
  };

  const _setPromoError = (promoError) => {
    setPromoError(promoError);
  };

  const getAssetType = (entityType) => {
    let assetType = "";
    if (
      recordType === ASSET_TYPE.SERIES ||
      recordType === ASSET_TYPE.SEASON ||
      (recordType === ASSET_TYPE.ID && entityType === "episode")
    ) {
      assetType = "episode";
    } else if (
      recordType === ASSET_TYPE.MOVIE ||
      (recordType === ASSET_TYPE.ID && entityType === "movie")
    ) {
      assetType = "feature";
    } else if (recordType === ASSET_TYPE.ID) {
      assetType = entityType;
    } else {
      assetType = recordType;
    }
    return assetType;
  };

  const getUniqueIds = () => {
    const ids = idTypeValues
      .split(/[\n,;]/)
      .map((str) => str.trim())
      .filter((id) => id);
    function uniq(a) {
      let seen = {};
      return a.filter(function (item) {
        return seen.hasOwnProperty(item) ? false : (seen[item] = true);
      });
    }
    return uniq(ids);
  };

  const getSourceId = (manifestationObj) => {
    const uniqueIdsList = getUniqueIds();
    if(uniqueIdsList.includes(manifestationObj?.editId?.id)) {
      return manifestationObj?.editId?.id;
    }
    else if(uniqueIdsList.includes(manifestationObj?.alternateIds?.[0]?.id)) {
      return manifestationObj?.alternateIds?.[0]?.id;
    }
    else {
      return manifestationObj?.alternateIds
        ? manifestationObj?.alternateIds[0]?.id
        : manifestationObj?.editId?.id;
    }
  };

  const getSourceIdNamespace = (manifestationObj) => {
    const uniqueIdsList = getUniqueIds();
    if(uniqueIdsList.includes(manifestationObj?.editId?.id)) {
      return manifestationObj?.editId?.idNamespace;
    }
    else if(uniqueIdsList.includes(manifestationObj?.alternateIds?.[0]?.id)) {
      return manifestationObj?.alternateIds?.[0]?.idNamespace;
    }
    else {
      return manifestationObj?.alternateIds
        ? manifestationObj?.alternateIds[0]?.idNamespace
        : manifestationObj?.editId?.idNamespace;
    }
  };

  const getSelectedContainer = (manifestation, file) => {
    return (manifestation?.containers || []).find((container) => {
      if (!container?.path || !container?.containerId) {
        return false;
      }
      const format = container.path.split(".").pop();
      return file === `${container.containerId}.${format}`;
    });
  };

  const handleSelectFile = (e, file, type, manifestation, assetTitle, editRate, entityType, idType = null) => {
    let selectedFileDetails = {
      ...selectedFiles,
    };
    if (e.target.checked) {
      const found = (file in selectedFiles);
      if (found) {
        selectedFileDetails[file]["isChecked"] = true;
        if (transferRegion[manifestation?.id] === "GCSSNY-MX_Content App") {
          const container = getSelectedContainer(manifestation, file);
          if (container?.path) {
            selectedFileDetails[file]["sourceUri"] = container.path;
            selectedFileDetails[file]["containerId"] = container.containerId;
          }
        }
      } else {
        let alternateId = getSourceId(manifestation);
        let idNamespace = getSourceIdNamespace(manifestation);
        const manifestationId = manifestation?.id;
        if (type === "Proxy Video") {
          idNamespace = manifestation?.idNamespace;
          idType = NAMESPACE_TO_ID_TYPE_MAPPING[idNamespace];
          alternateId = manifestationId;
        }
        let renameString = "";
        if (idType !== "dmi" && idType !== "cantemo" && idType !== "cantemofocusid" && idType !== "bim" && idType !== "bimcid") {
          const assetType = getAssetType(entityType);
          renameString = _.isNil(assetType) ? `${assetTitle.replace(/\s+/g, '')}_${editRate}` : `${assetTitle.replace(/\s+/g, '')}_${assetType}_${editRate}`;
        }
        const isGcssnyPackage = transferRegion[manifestationId] === "GCSSNY-MX_Content App";
        const container = isGcssnyPackage ? getSelectedContainer(manifestation, file) : null;
        selectedFileDetails = {
          ...selectedFileDetails,
          [file]: {
            id: alternateId,
            namespace: idNamespace,
            idType: NAMESPACE_TO_ID_TYPE_MAPPING[idNamespace],
            fileType: type,
            title: assetTitle,
            transfer: transferRegion[manifestationId],
            renamePrefix: renameString,
            renamedFilename: renamedFilename[file],
            ...(isGcssnyPackage && {
              sourceUri: container?.path,
              containerId: container?.containerId,
            }),
            isChecked: true,
            isMamProxy: type === "Proxy Video",
            mamProxyId: type === "Proxy Video" ? manifestation.id : null,
            qmManifestationId: getQualifiedMasterId(manifestation),
          },
        };
      }
    } else {
      selectedFileDetails[file]["isChecked"] = false;
    }
    setSelectedFiles(selectedFileDetails);
  };

  const getAssetByMamProxyId = (mamProxyId) => {
    if (!mamProxyId) {
      return null;
    }

    return (Object.values(validAssets || {}) as any[]).find((asset: any) => {
      return asset?.manifestation?.proxyCalculated?.mamProxy?.id?.id === mamProxyId;
    }) || null;
  };

  const getQualifiedMasterId = (manifestation) => {
    if (manifestation?.manifestationType?.includes("qualified-master")) {
      return manifestation?.id;
    }

    if (manifestation?.manifestationType?.includes("mam-proxy")) {
      return getQualifiedMasterIdByMamProxyId(manifestation?.id);
    }

    return null;
  };

  const getQualifiedMasterIdByMamProxyId = (mamProxyId) => {
    const matchedAsset = getAssetByMamProxyId(mamProxyId);

    return matchedAsset?.manifestation?.id || null;
  };

  const fetchMAMProxy = (manifestationIds) => {
    setIsProxyLoading(true);
    return new Promise((resolve, reject) => {
      const { odsSearchMAMProxy } = config.apiUrls;
      const payload = {
        uniqueIds: manifestationIds,
        queryIdentifiers: ["urn:warnermedia:manifestation.warnermedia.com"],
        isProxy: true
      };
      post({
        url: odsSearchMAMProxy,
        data: payload,
      })
      .then((response) => {
        setIsProxyLoading(false);
        let proxyDataFound = {};
        if (response?.data?.queryCanonicalV2SearchManifestation?.edges.length) {
          for (const { node } of response.data.queryCanonicalV2SearchManifestation.edges) {
            const proxyManifestationId = node.manifestation.id;
            proxyDataFound = {...proxyDataFound, [proxyManifestationId]: node.manifestation};
          }
        }
        resolve(proxyDataFound);
      })
      .catch((error) => {
        setIsProxyLoading(false);
        console.log(error);
        reject(error);
      })
    });
  };

  // Only used to search all the seasons of a series
  const callSearchById = (guid) => {
    setIsEpisodeDataLoading((prev: any) => ({ ...prev, [guid]: true }));
    const { odsSearchByIds } = config.apiUrls;
    const payload = {
      uniqueIds: [guid],
      queryIdentifiers: ["urn:wbd:identifier:program-inventory:id"],
      queryIdentifierType: "programId",
    };
    post({
      url: odsSearchByIds,
      data: payload,
    })
      .then((response) => {
        setEpisodeDataError((prev: any) => ({ ...prev, [guid]: false }));
        setEpisodeData((prev: any) => ({ ...prev, [guid]: response.options }));
      })
      .catch((error) => {
        setEpisodeDataError((prev: any) => ({ ...prev, [guid]: true }));
        console.log(error);
      })
      .finally(() => {
        setIsEpisodeDataLoading((prev: any) => ({ ...prev, [guid]: false }));
      });
  };

  const getVideoFormat = (manifestation) => {
    let formatValue;
    let dynamicRangeValue = "";
    if (manifestation?.components) {
      for (const component of manifestation.components) {
        if (component?.componentType?.includes("video")) {
          formatValue = _.get(component, "attributes.frameSize.height", "");
          dynamicRangeValue = _.get(component, "attributes.dynamicRange", "");
          break;
        }
      }
    }
    if (formatValue <= 720) {
      formatValue = "SD";
    } else if (formatValue <= 1080 && formatValue > 720) {
      formatValue = "HD";
    } else if (formatValue <= 2160 && formatValue > 1080) {
      formatValue = "UHD";
    }
    if (dynamicRangeValue === "urn:warnermedia:video:dynamic-range:sdr") {
      dynamicRangeValue = "SDR";
    } else if (
      dynamicRangeValue === "urn:warnermedia:video:dynamic-range:hdr"
    ) {
      dynamicRangeValue = "HDR";
    }
    return `${formatValue} ${dynamicRangeValue}`;
  };

  const getFilesFromContainers = (manifestationObj) => {
    const files = [];
    manifestationObj?.containers?.forEach((container) => {
      if (container.path) {
        const pathSplit = container.path?.split(".");
        const format = pathSplit[pathSplit.length - 1];
        const file = `${container.containerId}.${format}`;
        files.push(file);
      }
    });
    return files;
  };

  const getMAMProxyIds = (responseData) => {
    const mamProxyIds = [];
    for (const { node } of responseData) {
      for (const relationship of node.relationships) {
        for (const manifestation of relationship.relatedEntityManifestations) {
          if (manifestation.proxyCalculated) {
            if (manifestation.proxyCalculated.mamProxy.exists === "true") {
              mamProxyIds.push(manifestation.proxyCalculated.mamProxy.id.id);
            }
          }
        }
      }
    }
    return mamProxyIds;
  };
  
  const fetchDMIAssets = (selectedOption) => {
    try {
      let idsToValidate = { };
      for (const manifestationObj of selectedOption.assets) {
        if (selectedOption.payload.showAbbr === manifestationObj.titleid) {
          manifestationObj.files = getFilesFromContainers(manifestationObj);
          manifestationObj.format = manifestationObj.attributes?.isHD === "Yes" ? "HD": "SD";
          manifestationObj.id = manifestationObj.cid;
          const manifestationId = manifestationObj.cid;
          manifestationObj.alternateIds = [{
            id: manifestationObj.cid,
            idNamespace: "dmicid",
          }];
          manifestationObj.editId = {
            id: manifestationObj.titleid,
            idNamespace: "dmititleid",
          };
          if (!idsToValidate.hasOwnProperty(manifestationId)) {
            idsToValidate = {
              ...idsToValidate,
              [manifestationId]: {
                manifestation: manifestationObj.files?.length > 0 ? manifestationObj: {},
                displayTitle: manifestationObj.attributes?.title ? manifestationObj.attributes?.title : manifestationObj.cid,
                entityType: "episode",
                episodeNumber: manifestationObj.attributes?.episodeNumber ? manifestationObj.attributes?.episodeNumber : "",
                idType: "dmi"
              },
            };
          }
        }
      }
      _setValidAssets(idsToValidate);
      history.push(CREATE_MEDIA.route);
    } catch(error) {
      console.log(error);
      setPromoError({title: "Network Error", message: "Error in finding DMI Data"});
    } finally {
      setIsPromoLoading(false);
    }
  };

  const addCantemoManifestation = (idsToValidate, manifestationObj, displayTitleFallback) => {
    normalizeCantemoComponents(manifestationObj);
    if (!manifestationObj?.components?.some((component) => component.componentType)) {
      return idsToValidate;
    }
    manifestationObj.files = getFilesFromContainers(manifestationObj);
    manifestationObj.format = manifestationObj.attributes?.fileFormat;
    const manifestationId = manifestationObj.attributes?.itemId ?? manifestationObj.containers?.[0]?.containerId;
    if (!manifestationId || idsToValidate.hasOwnProperty(manifestationId)) {
      return idsToValidate;
    }
    manifestationObj.id = manifestationId;
    manifestationObj.alternateIds = [{
      id: manifestationObj.focusId,
      idNamespace: "cantemofocusid",
    }];
    return {
      ...idsToValidate,
      [manifestationId]: {
        manifestation: manifestationObj.files?.length > 0 ? manifestationObj : {},
        displayTitle: manifestationObj.attributes?.title ?? displayTitleFallback,
        entityType: "episode",
        episodeNumber: manifestationObj.attributes?.episodeNumber ?? "",
        idType: "cantemo"
      },
    };
  };

  const buildCantemoValidAssets = (manifestationsById, existingAssets = {}) => {
    let idsToValidate = { ...existingAssets };
    Object.keys(manifestationsById).forEach((id) => {
      manifestationsById[id].forEach((manifestationObj) => {
        idsToValidate = addCantemoManifestation(idsToValidate, manifestationObj, id);
      });
    });
    return idsToValidate;
  };

  const fetchCantemoAssets = (selectedOption) => {
    try {
      let idsToValidate = {};
      selectedOption.assets
        .filter((manifestationObj) => selectedOption.payload.focusId === manifestationObj.focusId)
        .forEach((manifestationObj) => {
          idsToValidate = addCantemoManifestation(idsToValidate, manifestationObj, manifestationObj.focusId);
        });
      _setValidAssets(idsToValidate);
      history.push(CREATE_MEDIA.route);
    } catch(error) {
      console.log(error);
      setPromoError({title: "Network Error", message: "Error in finding Cantemo Data"});
    } finally {
      setIsPromoLoading(false);
    }
  };

  const findComponentBySuffix = (components, altId) => {
    const segmentSuffix = altId.split("_")[1];
    if (!segmentSuffix) {
      return components?.find(({ trackReference }) => {
        return trackReference.containerId.endsWith(altId);
      });
    }
    return components?.find(({ trackReference }) => {
      return trackReference.containerId.endsWith(`_${segmentSuffix}`);
    });
  };

  const findContainerBySuffix = (containers, altId) => {
    const segmentSuffix = altId.split("_")[1];
    if (!segmentSuffix) {
      return containers?.find(({ containerId }) => {
        return containerId.endsWith(altId);
      });
    }
    return containers?.find(({ containerId }) => {
      return containerId.endsWith(`_${segmentSuffix}`);
    });
  };

  const processMaterialData = (materialData) => {
    const {
      materialId,
      alternateMaterialId = [],
      manifestationType,
      components = [],
      containers = []
    } = materialData;

    const transformed = {};

    alternateMaterialId.forEach((altId) => {
      const component = findComponentBySuffix(components, altId);
      const container = findContainerBySuffix(containers, altId);

      transformed[altId] = {
        materialId,
        alternateMaterialId: altId,
        alternateMaterialIds: alternateMaterialId,
        manifestationType,
        components: component ? [component] : [],
        containers: container ? [container] : [],
      };
    });

    return transformed;
  };

  const transformFbdApiResponse = (response = {}) => {
    return Object.values(response).reduce((acc, { data }) => {
      if (!Array.isArray(data)) {
        return acc;
      }

      data.forEach((materialData) => {
        Object.assign(acc, processMaterialData(materialData));
      });

      return acc;
    }, {});
  };

  const fetchOdsForCutCid = async (uniqueIdsList, odsSearchByIds) => {
    const idType = ["urn:warnermedia:manifestation.warnermedia.com"];
    const idQueryType = "manifestationId";
    const payload = {
      uniqueIds: uniqueIdsList,
      queryIdentifiers: idType,
      queryIdentifierType: idQueryType
    };
    const response = await post({ url: odsSearchByIds, data: payload });
    let proxyData = {};
    const edges = response.data.queryCanonicalV4SearchAsset.edges || [];

    if (edges.length) {
      const mamProxyIds = getMAMProxyIds(edges);
      if (mamProxyIds?.length) {
        proxyData = await fetchMAMProxy(mamProxyIds);
      }
    }

    return {
      assetOptions: response.options,
      odsData: { data: response.data, proxyData },
    };
  };

  const getNonProxyManifestations = (manifestations) => {
    if (!Array.isArray(manifestations)) return [];
    return manifestations.filter(
      (m) => !PROXY_MANIFESTATION_TYPES.includes(m.manifestationType)
    );
  };

  const collectQmIds = (validCUTids) => {
    const ids = [];
    for (const records of Object.values(validCUTids) as any) {
      for (const record of records) {
        const qm = getNonProxyManifestations(record?.manifestations || []);
        for (const m of qm) ids.push(m.manifestationId);
      }
    }
    return ids;
  };

  const handleCutCidResponse = async (validCUTids, odsSearchByIds) => {
    const allIdsToQuery = collectQmIds(validCUTids);

    if (!allIdsToQuery.length) {
      setAssetData([]);
      setIsAssetDataLoading(false);
      return;
    }

    try {
      const { assetOptions, odsData } = await fetchOdsForCutCid(allIdsToQuery, odsSearchByIds);
      setAssetData(assetOptions);
      setOdsSearchIdsData(odsData);
    } catch (error) {
      console.log(error);
      setAssetData("idsError");
    } finally {
      setIsAssetDataLoading(false);
    }
  };

  const searchMediaById = () => {
    const { odsSearchByIds, validateMultipleEdits } = config.apiUrls;
    const uniqueIdsList = getUniqueIds();
    if (_.isEqual(idType, ["pavi", "pafi"])) {
      const payload = [];
      uniqueIdsList.forEach((promoId) => {
        payload.push({ id: promoId, idType: "pavi", searchInactive: false });
        payload.push({ id: promoId, idType: "pafi", searchInactive: false });
      });
      post({
        url: validateMultipleEdits,
        data: payload,
      })
        .then((response) => {
          const validPromos = response?.details?.success;
          let idsToValidate = { ...validAssets };

          Object.keys(validPromos).forEach((id) => {
            for (const manifestationObj of validPromos[id]) {
              manifestationObj.files = getFilesFromContainers(manifestationObj);
              manifestationObj.format = getVideoFormat(manifestationObj);
              const manifestationId = manifestationObj.id;
              if (!idsToValidate.hasOwnProperty(manifestationId)) {
                idsToValidate = {
                  ...idsToValidate,
                  [manifestationId]: {
                    manifestation: manifestationObj,
                    displayTitle: id,
                  },
                };
              }
            }
          });
          _setValidAssets(idsToValidate);
          history.push(CREATE_MEDIA.route);
        })
        .catch((error) => {
          console.log(error);
          if (error?.response?.status === 400) {
            setPromoError({title: "Not Found", message: "No manifestation found for entered HBO Promo PAVI"});
          } else {
            setPromoError({title: "Network Error", message: "Error in finding HBO Promo PAVI"});
          }
        })
        .finally(() => {
          setIsPromoLoading(false);
        });
    } else if (_.isEqual(idType, ["dmicid"]) || _.isEqual(idType, ["dmititleid"])) {
      const payload = [];
      if (_.isEqual(idType, ["dmicid"])) {
        uniqueIdsList.forEach((dmiId) => {
          payload.push({ id: dmiId, idType: "dmicid", searchInactive: false });
        });
      } else {
        uniqueIdsList.forEach((dmiId) => {
          payload.push({
            id: dmiId,
            idType: "dmititleid",
            searchInactive: false,
          });
        });
      }
      post({
        url: validateMultipleEdits,
        data: payload,
      })
        .then((response) => {
          const validDMIs = response?.details?.success;
          let idsToValidate = { ...validAssets };

          Object.keys(validDMIs).forEach((id) => {
            for (const manifestationObj of validDMIs[id]) {
              manifestationObj.files = getFilesFromContainers(manifestationObj);
              manifestationObj.format = manifestationObj.attributes?.isHD === "Yes" ? "HD": "SD";
              manifestationObj.id = manifestationObj.cid;
              const manifestationId = manifestationObj.cid;
              manifestationObj.alternateIds = [{
                id: manifestationObj.cid,
                idNamespace: "dmicid",
              }];
              manifestationObj.editId = {
                id: manifestationObj.titleid,
                idNamespace: "dmititleid",
              };
              if (!idsToValidate.hasOwnProperty(manifestationId)) {
                idsToValidate = {
                  ...idsToValidate,
                  [manifestationId]: {
                    manifestation: manifestationObj.files?.length > 0 ? manifestationObj: {},
                    displayTitle: manifestationObj.attributes?.title ? manifestationObj.attributes?.title : id,
                    entityType: "episode",
                    episodeNumber: manifestationObj.attributes?.episodeNumber ? manifestationObj.attributes?.episodeNumber : "",
                    idType: "dmi"
                  },
                };
              }
            }
          });
          _setValidAssets(idsToValidate);
          history.push(CREATE_MEDIA.route);
        })
        .catch((error) => {
          console.log(error);
          if (error?.response?.status === 400) {
            setPromoError({title: "Not Found", message: "No files found for entered DMI ID"});
          } else {
            setPromoError({title: "Network Error", message: "Error in finding DMI Data"});
          }
        })
        .finally(() => {
          setIsPromoLoading(false);
        });
    } else if (_.isEqual(idType, ["cantemofocusid"])) {
      setIsCantemoLoading(true);
      const payload = [];
      uniqueIdsList.forEach((cantemoFocusId) => {
        payload.push({
          id: cantemoFocusId,
          idType: "cantemofocusid",
          searchInactive: false,
        });
      });
      post({
        url: validateMultipleEdits,
        data: payload,
      })
        .then((response) => {
          _setCantemoPage(0);
          setCantemoTotal(null);
          _setValidAssets(buildCantemoValidAssets(response?.details?.success, validAssets));
          history.push(CREATE_MEDIA.route);
        })
        .catch((error) => {
          console.log(error);
          if (error?.response?.status === 400) {
            setPromoError({title: "Not Found", message: "No files found for entered Cantemo ID"});
          } else {
            setPromoError({title: "Network Error", message: "Error in finding Cantemo Data"});
          }
        })
        .finally(() => {
          setIsPromoLoading(false);
          setIsCantemoLoading(false);
        });
    } else if (recordType === ASSET_TYPE.CANTEMO) {
      setIsCantemoLoading(true);
      const payload = buildCantemoValidatePayload(cantemoFilters);
      post({
        url: validateMultipleEdits,
        data: payload,
      })
        .then((response) => {
          if (applyCantemoFilterResults(response?.details?.success, response?.details?.cantemoTotals, 0)) {
            history.push(CREATE_MEDIA.route);
          }
        })
        .catch((error) => {
          console.log(error);
          if (error?.response?.status === 400) {
            setPromoError({title: "Not Found", message: "No files found for entered Cantemo value"});
          } else {
            setPromoError({title: "Network Error", message: "Error in finding Cantemo Data"});
          }
        })
        .finally(() => {
          setIsPromoLoading(false);
          setIsCantemoLoading(false);
        });
    } else if (_.isEqual(idType, ["bimcid"])) {
      const payload = [];
      uniqueIdsList.forEach((bimcid) => {
        payload.push({
          id: bimcid,
          idType: "bimcid",
          searchInactive: false,
          start: 1
        });
      });
      post({
        url: validateMultipleEdits,
        data: payload,
      })
        .then((response) => {
          const validBIMs = transformFbdApiResponse(response?.details?.success); 
          let idsToValidate = { ...validAssets };
          Object.values(validBIMs).forEach(manifestationObj => {
            manifestationObj.files = getFilesFromContainers(manifestationObj);
            manifestationObj.id = manifestationObj.alternateMaterialId;
            const manifestationId = manifestationObj.alternateMaterialId;
            manifestationObj.alternateIds = [{
              id: manifestationObj.alternateMaterialId,
              idNamespace: "bimcid",
            }];
            if (!idsToValidate[manifestationId]) {
              idsToValidate[manifestationId] = {
                manifestation: manifestationObj.files?.length > 0 ? manifestationObj : {},
                displayTitle: manifestationObj.containers[0]?.attributes?.title ?? manifestationObj.alternateMaterialId,
                entityType: "episode",
                episodeNumber: manifestationObj.containers[0]?.attributes?.episodeNumber ?? "",
                idType: "bim"
              };
            }
          });
          _setValidAssets(idsToValidate);
          history.push(CREATE_MEDIA.route);
        })
        .catch((error) => {
          console.log(error);
          if (error?.response?.status === 400) {
            setPromoError({title: "Not Found", message: "No files found for entered BIM CID"});
          } else {
            setPromoError({title: "Network Error", message: "Error in finding Bim Data"});
          }
        })
        .finally(() => {
          setIsPromoLoading(false);
        });
    } else if (_.isEqual(idType, ["cutcid"])) {
      setSelectedOption({ payload: { title: "Displaying Results" } });
      setAssetData("loading");
      const cutcidPayload = uniqueIdsList.map((cutcid) => ({
        id: cutcid,
        idType: "cutcid",
        searchInactive: false,
      }));
      post({
        url: validateMultipleEdits,
        data: cutcidPayload,
      })
      .then((response) =>
        handleCutCidResponse(response?.details?.success, odsSearchByIds)
      )
      .catch((error) => {
        console.log(error);
        setAssetData([])
      })
      .finally(() => {
        setIsPromoLoading(false);
        setIsAssetDataLoading(false);
      });
    } else {
          setSelectedOption({ payload: { title: "Displaying Results" } });
          setAssetData("loading");
          const payload = {
            uniqueIds: uniqueIdsList,
            queryIdentifiers: idType,
            queryIdentifierType: idQueryType,
          };
          post({
            url: odsSearchByIds,
            data: payload,
          })
            .then(async (response) => {
              let mamProxyIds, proxyData = {};
              if (response.data.queryCanonicalV4SearchAsset.edges.length) {
                mamProxyIds = getMAMProxyIds(response.data.queryCanonicalV4SearchAsset.edges);
                if (mamProxyIds?.length) {
                  proxyData = await fetchMAMProxy(mamProxyIds);
                }
              }
              const responseData = {data: response?.data, proxyData};
              setAssetData(response.options);
              setOdsSearchIdsData(responseData);
            })
            .catch((error) => {
              setAssetData("idsError");
              console.log(error);
            })
            .finally(() => {
              setIsAssetDataLoading(false);
              setIsPromoLoading(false);
            });
        }
    };

  const fetchEdits = (guid) => {
    return new Promise((resolve, reject) => {
      const { odsSearchByIds } = config.apiUrls;
      const payload = {
        uniqueIds: [guid],
        queryIdentifiers: ["urn:wbd:identifier:program-inventory:id"],
        queryIdentifierType: "programId",
      };
      post({
        url: odsSearchByIds,
        data: payload,
      })
        .then( async (response) => {
          let mamProxyIds, proxyData = {};
          if (response?.data?.queryCanonicalV4SearchAsset?.edges.length) {
            mamProxyIds = getMAMProxyIds(response.data.queryCanonicalV4SearchAsset.edges);
            if (mamProxyIds?.length > 0) {
              proxyData = await fetchMAMProxy(mamProxyIds);
            }
          }
          setOdsEditsFetchError({ ...odsEditsFetchError, [guid]: false });
          const edits = response?.data?.queryCanonicalV4SearchAsset?.edges.length
            ? {
                ...odsEditsData,
                [guid]: response?.data?.queryCanonicalV4SearchAsset?.edges,
              }
            : { ...odsEditsData };
          resolve({edits, proxyData});
        })
        .catch((error) => {
          console.log(error);
          setOdsEditsFetchError({ ...odsEditsFetchError, [guid]: true });
          reject(error);
        });
    });
  };

  const formatRelationshipValues = (relationship, relatedRecords, proxyData) => {
    relationship?.relatedEntityManifestations?.forEach((manifestation) => {
      if (manifestation["controllingNetwork"] === "") {
        manifestation["controllingNetwork"] = relatedRecords?.originatingBrand?.[0]?.brandName || "";
      }
      const proxyCalculated = manifestation["proxyCalculated"];
      if (proxyCalculated?.mamProxy?.exists === "true") {
        const mamProxyId = proxyCalculated["mamProxy"]["id"]["id"];
        manifestation["mamProxy"] = proxyData[mamProxyId];
      }
      manifestation.files = getFilesFromContainers(manifestation);
      manifestation.format = getVideoFormat(manifestation);
    });
    return relationship;
  };

  const findEpisodeNumber = (alternateIdsList) => {
    let episode = "";
    if (alternateIdsList) {
      for (const obj of alternateIdsList) {
        if (
          obj["namespace"] ===
          "urn:wbd:identifier:program-inventory:episode-number"
        ) {
          episode = obj["id"];
          break;
        }
      }
    }
    return episode;
  };
  
  const fetchEditsFromODS = async (relatedRecords, guid) => {
    setIsOdsEditsDataLoading({ ...isOdsEditsDataLoading, [guid]: true });
    if (recordType === ASSET_TYPE.ID) {
      setIsOdsEditsDataLoading({ ...isOdsEditsDataLoading, [guid]: false });
      setOdsEditsFetchError({ ...odsEditsFetchError, [guid]: false });
      let edits = { ...odsEditsData };
      odsSearchIdsData["data"]?.queryCanonicalV4SearchAsset?.edges?.forEach(({ node }) => {
        if (node.id?.id === guid) {
          if (guid in edits) {
            edits[guid].push({ node });
          } else {
            edits = {
              ...edits,
              [guid]: [{ node }],
            };
          }
        }
      });
      if (edits && guid in edits) {
        for (const { node } of edits[guid]) {
          if (_.isEmpty(node?.relationships)) {
            edits = _.omit(edits, guid);
          } else {
            node?.relationships?.forEach((relationship) => {
              relationship = formatRelationshipValues(relationship, relatedRecords, odsSearchIdsData["proxyData"]);
              relationship["entityType"] = node.entityType;
            });
            const episode = findEpisodeNumber(node?.alternateIds);
            node["episodeNumber"] = episode;
          }
        }
      }
      setOdsEditsData(edits);
    } else {
      fetchEdits(guid)
        .then(({edits, proxyData}) => {
          if (edits && guid in edits) {
            for (const { node } of edits[guid]) {
              if (_.isEmpty(node?.relationships)) {
                edits = _.omit(edits, guid);
              } else {
                node?.relationships?.forEach((relationship) => {
                  formatRelationshipValues(relationship, relatedRecords, proxyData);
                });
                if (node?.entityType !== "movie") {
                  const episode = findEpisodeNumber(node?.alternateIds);
                  node["episodeNumber"] = episode;
                }
              }
            }
          }
          setOdsEditsData(edits);
        })
        .catch((error) => {
          console.log("error: ", error);
          setOdsEditsFetchError({ ...odsEditsFetchError, [guid]: true });
        })
        .finally(() => {
          setIsOdsEditsDataLoading({ ...isOdsEditsDataLoading, [guid]: false });
        });
    }
  };

  const sortByName = (data) => {
    let sortedData;
    sortedData = data.sort(function (a, b) {
      let x = a.relatedEntityDisplayTitle.toLowerCase();
      let y = b.relatedEntityDisplayTitle.toLowerCase();
      if (x > y) {
        return 1;
      }
      if (x < y) {
        return -1;
      }
      return 0;
    });

    return sortedData;
  };

  const sortByEpisodeNumber = (options) => {
    let sortedData;
    sortedData = options.sort(function (a, b) {
      let x = Number(a.asset.inheritedFields.episodeSequence);
      let y = Number(b.asset.inheritedFields.episodeSequence);
      if (x > y) {
        return 1;
      }
      if (x < y) {
        return -1;
      }
      return 0;
    });
    return sortedData;
  };

  const fetchAssets = (selectedOption) => {
    if (recordType === ASSET_TYPE.EPISODE || recordType === ASSET_TYPE.PROMO) {
      const assets = [{ ...selectedOption }];
      setAssetData(assets);
      setOdsEditsData(initialState.odsEditsData);
      setIsAssetDataLoading(false);
    } else if (recordType === ASSET_TYPE.SERIES) {
      setIsSeasonDataLoading(true);
      const payload = {
        uniqueIds: [selectedOption?.asset?.ids?.guid],
        queryIdentifiers: ["urn:wbd:identifier:program-inventory:id"],
      };
      const { odsFetchSeasons } = config.apiUrls;
      post({
        url: odsFetchSeasons,
        data: payload,
      })
        .then((response) => {
          let seasonsList = [];
            response?.data?.queryCanonicalV4SearchAsset?.edges?.forEach(({ node }) => {
            for (const relationship of node.relationships) {
              if (relationship.relatedEntityType === "season") {
                seasonsList.push(relationship);
              }
            }
          });
          seasonsList = sortByName(seasonsList);
          setOdsEditsData(initialState.odsEditsData);
          setEpisodeData(initialState.episodeData);
          setEpisodeDataError(initialState.episodeDataError);
          setIsEpisodeDataLoading(initialState.isEpisodeDataLoading);
          setSeasonData(seasonsList);
        })
        .catch((error) => {
          console.log(error);
          setSeasonDataError("Error in finding seasons");
        })
        .finally(() => {
          setIsSeasonDataLoading(false);
        });
    } else {
      setAssetData("loading");
      const payload = {
        uniqueIds: [selectedOption?.asset?.ids?.guid],
        queryIdentifiers: ["urn:wbd:identifier:program-inventory:id"],
        queryIdentifierType: "programId",
      };
      const { odsSearchByIds } = config.apiUrls;
      post({
        url: odsSearchByIds,
        data: payload,
      })
        .then((response) => {
          setOdsEditsData(initialState.odsEditsData);
          const sortedEpisodeData = sortByEpisodeNumber(response.options);
          setAssetData(sortedEpisodeData);
        })
        .catch((error) => {
          console.log(error);
          setAssetData("error");
        })
        .finally(() => {
          setIsAssetDataLoading(false);
        });
    }
  };

  const _setSuggestString = (suggestString) => {
    setSuggestString(suggestString);
  };

  const fetchNetworks = () => {
    if (sessionStorage.getItem(MEDIA_SEARCH.storageKey)) {
      return;
    }
    setLoading(true);
    get({ url: config.apiUrls.brandNames })
      .then((response) => {
        const brandNameMapping = {};
        response.forEach((network) => {
          const key = network.abbreviation;
          const value = network.shortName;
          brandNameMapping[key] = value;
        });
        sessionStorage.setItem(
          MEDIA_SEARCH.storageKey,
          JSON.stringify(brandNameMapping)
        );
        setPageError(false);
      })
      .catch((error) => {
        console.log(error);
        setPageError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const generateOptionsForDMITitleSearch = (response) => {
    const options = [];
    for (const element of response) {
      const option = {
        payload: {
          brand: element.attributes.originatingNetwork,
          brandAbbr: element.attributes.originatingNetwork,
          propertyId: element.attributes.releaseYear,
          recordType: "dmiTitle",
          scrid: element.attributes.barcode,
          showAbbr: element.titleid,
          title: element.attributes.title,
          titleType: element.attributes.titleType
        },
        assets: response
      }
      options.push(option);
    }
    return options;
  };

  const fetchSearchOptions = (value, recordType) => {
    setNoOptionsFound(false);
    if (recordType === ASSET_TYPE.DMI_TITLE) {
      const body = [{
        id: value,
        idType: "dmititle",
        searchInactive: false,
      }];
      post({ url: config.apiUrls.validateMultipleEdits, data: body, signal: true })
        .then((response) => {
          const options = generateOptionsForDMITitleSearch(response.details.success[value]);
          if (_.isEmpty(options)) {
            setNoOptionsFound(true);
          }
          _setOptions(options);
        })
        .catch((err) => {
          _setOptions([]);
          setNoOptionsFound(true);
          console.log(err);
        });
    } else {
      const entityClassValue = [];
      if (recordType === ASSET_TYPE.SERIES) {
        entityClassValue.push("ENTITY_CLASS_SERIES");
      } else if (recordType === ASSET_TYPE.SEASON) {
        entityClassValue.push("ENTITY_CLASS_SEASON");
      } else {
        entityClassValue.push("ENTITY_CLASS_PROGRAM");
      }
      const body = {
        searchString: [value],
        entityClass: entityClassValue,
        entityType: recordType === ASSET_TYPE.PROMO ?  ["promo", "promotional"] : [recordType]
      };
      post({ url: config.apiUrls.odsAutosuggest, data: body, signal: true })
        .then((response) => {
          if (_.isEmpty(response)) {
            setNoOptionsFound(true);
          }
          _setOptions(response);
        })
        .catch((err) => {
          _setOptions([]);
          setNoOptionsFound(true);
          console.log(err);
        });
    }
    
  };

  const _setValidAssets = (asset) => {
    setValidAssets(asset);
  };

  const _setRequestName = (requestName) => {
    setRequestName(requestName);
  };

  const _setIsMrSubmitError = (isMrSubmitError) => {
    setIsMrSubmitError(isMrSubmitError);
  };

  const regionMap = regionDetails.reduce((map, region) => {
    map[region.name] = region;
    return map;
  }, {});

  const getPath = (regionName) => {
    const { environment } = config.app;
    const sAMAccountName = _.get(userInfo, "sAMAccountName");
    let path = REGION_PATHS[regionName];

    if (!path) {
      path = `/${environment}/${sAMAccountName}`; // fallback
    }

    if (typeof path === "object") {
      path = environment === "prod" ? path.prod : path.nonProd;
    }

    if (path.includes("{{environment}}")) {
      path = path.replace("{{environment}}", environment);
    }
    if (path.includes("{{sAMAccountName}}")) {
      path = path.replace("{{sAMAccountName}}", sAMAccountName);
    }
    if (path.includes("{{requestName}}")) {
      path = path.replace("{{requestName}}", requestName);
    }

    return path;
  };

  const getBatchMediaRequestPayload = () => {
    const { environment } = config.app;
    const envType = environment === "prod" ? "prod" : "non-prod";
    const body = {
      "jobType": CREATE_MEDIA.jobType,
      "name": requestName,
      "requestor": {
        "email": _.get(userInfo, "email"),
        "fullName": _.get(userInfo, "given_name") + " " + _.get(userInfo, "family_name"),
        "deptName": _.get(userInfo, "department", "MSE"),
        "sAMAccountName": _.get(userInfo, "sAMAccountName"),
        "role": userRole,
        "oktaUid": _.get(userInfo, "sub")
      },
      "payload": []
    };
    const jobsArray = [];

    const packageFiles = [];

    Object.keys(saveToCart).forEach((file) => {
      const fileInfo = saveToCart[file];
      if (fileInfo.transfer === "GCSSNY-MX_Content App") {
        packageFiles.push({ file, fileInfo });
        return;
      }
      const region = regionMap[fileInfo.transfer];
      const configId = region.configId;
      const dropboxName = region.dropboxName || "";
      const workspaceId = aocConfigs?.[configId]?.[envType]?.workspaceId || "";

      const sourceId = fileInfo.id;
      const mamProxyId =
        fileInfo.mamProxyId ||
        (fileInfo.isMamProxy ? sourceId : null);
      const qmManifestationId =
        fileInfo.qmManifestationId ||
        getQualifiedMasterIdByMamProxyId(mamProxyId);
      const files = [file];

      let job = jobsArray.find(
        (j) => j.sources?.[0]?.id === sourceId
      );

      if (job) {
        job.files.push(file);

        if (fileInfo.isMamProxy) {
          job.isMamProxy = true;
        }

        if (!job.mamProxyId && mamProxyId) {
          job.mamProxyId = mamProxyId;
        }

        if (!job.qmManifestationId && qmManifestationId) {
          job.qmManifestationId = qmManifestationId;
        }
      } else {
        jobsArray.push({
          sources: [
            {
              id: sourceId,
              namespace: fileInfo.namespace,
              idType: fileInfo.idType,
            },
          ],

          delivery: {
            id: configId,
            attributes: {
              path: getPath(fileInfo.transfer),
              dropboxName,
              workspaceId,
              packageName: requestName,
            },
          },

          isValidated: true,
          files,
          qmManifestationId,
          mamProxyId,
          renamePrefix: fileInfo.renamePrefix,
          isMamProxy: fileInfo.isMamProxy,
        });
      }
    });

    if (packageFiles.length) {
      const region = regionMap["GCSSNY-MX_Content App"];
      const configId = region.configId;
      const dropboxName = region.dropboxName || "";
      const workspaceId = aocConfigs?.[configId]?.[envType]?.workspaceId || "";
      const packageSource = packageFiles[0].fileInfo;
      jobsArray.push({
        sources: [{
          id: "",
          namespace: packageSource.namespace,
          idType: packageSource.idType,
        }],
        delivery: {
          id: configId,
          attributes: {
            path: getPath("GCSSNY-MX_Content App"),
            dropboxName,
            workspaceId,
            packageName: requestName,
          },
        },
        isValidated: true,
        files: packageFiles.map(({ file }) => file),
        fileOverrides: packageFiles.map(({ file, fileInfo }) => {
          const renamed = fileInfo.renamedFilename || file;
          const dot = renamed.lastIndexOf(".");
          const sourceUri = fileInfo.sourceUri || "";
          return {
            source_uri: !sourceUri || sourceUri.startsWith("s3://") ? sourceUri : `s3://${sourceUri}`,
            container_id: fileInfo.containerId,
            override_filename: dot > 0 ? renamed.slice(0, dot) : renamed,
          };
        }),
      });
    }

    body.payload = jobsArray;
    return body;
  };

  const submitMediaRequest = () => {
    setLoading(true);
    const { createBatchMediaRequest } = config.apiUrls;
    const payload = getBatchMediaRequestPayload();
    post({
      url: createBatchMediaRequest,
      data: payload,
    })
      .then((response) => {
        const submitJobSocketMessage = SocketUtils.buildSocketMessage('NEW_REQUEST');
        _setMessageToSocket(submitJobSocketMessage);
        history.push("/");
      })
      .catch((error) => {
        console.log(error);
        setIsMrSubmitError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MediaRequestContext.Provider
      value={{
        loading,
        pageError,
        fetchNetworks,
        fetchSearchOptions,
        options,
        _setOptions,
        _setRecordType,
        recordType,
        selectedOption,
        _setSelectedOption,
        assetData,
        isAssetDataLoading,
        fetchAssets,
        suggestString,
        _setSuggestString,
        validAssets,
        _setValidAssets,
        videoContainerId,
        _setVideoContainerId,
        selectedFiles,
        _setSelectedFiles,
        handleSelectFile,
        activeStep,
        _setActiveStep,
        idAccordionExpanded,
        _setIdAccordionExpanded,
        titleAccordionExpanded,
        _setTitleAccordionExpanded,
        transferRegion,
        _setTransferRegion,
        submitMediaRequest,
        requestName,
        _setRequestName,
        renamedFilename,
        _setRenamedFilename,
        saveToCart,
        _setSaveToCart,
        odsEditsData,
        _setOdsEditsData,
        isOdsEditsDataLoading,
        odsEditsFetchError,
        fetchEditsFromODS,
        isMrSubmitError,
        _setIsMrSubmitError,
        idType,
        _setIdType,
        idQueryType,
        _setIdQueryType,
        idTypeValues,
        _setIdTypeValues,
        cantemoFilters,
        _setCantemoFilters,
        cantemoPage,
        _setCantemoPage,
        cantemoTotal,
        searchCantemoPage,
        searchMediaById,
        _setOdsSearchIdsData,
        fetchEdits,
        getAssetType,
        seasonData,
        seasonDataError,
        isSeasonDataLoading,
        callSearchById,
        episodeData,
        episodeDataError,
        isEpisodeDataLoading,
        _setSeasonData,
        _setEpisodeData,
        isPromoLoading,
        _setIsPromoLoading,
        isCantemoLoading,
        _setIsCantemoLoading,
        promoError,
        _setPromoError,
        isProxyLoading,
        noOptionsFound,
        getSourceId,
        getSourceIdNamespace
      }}
    >
      {_.toLower(userRole) === UserRoles.MEDIAREQUEST ||
      userRole === UserRoles.SUPERADMIN ||
      _.toLower(userRole) === UserRoles.GLOBALADMIN ||
      userRole === UserRoles.REQUESTOR ||
      userRole === UserRoles.OPS ? (
        children
      ) : (
        <ErrorRedirect
          error={{
            errorCode: "access_denied",
            type: "restricted",
            message: "User is not authorized to access this page.",
            redirect: _.toLower(userRole) === UserRoles.CR_API ? true : false,
          }}
        />
      )}
      <LoadingSpinner show={loading || isUserLoading} />
    </MediaRequestContext.Provider>
  );
};
